import { Teksten } from "../content";
import { DropdownInfoFactory, WaardelijstFactory } from "../factories";
import { IdentifierWithChildren } from "../factories/waardelijst";
import { Identifier } from "../models";
import { DossierMetaData } from "../models/dossier-metadata";
import { AppDispatch } from "../store";
import {
  updateCreatieDatum,
  updateDossierType,
  updateIdentifiers,
  updateOmschrijving,
  updatePublisher,
  updateThemas,
  updateTitel,
} from "../store/reducers/dossier-metadata";
import { FormConfig } from "../ui/interfaces/FormConfig";
import { createValidators } from "../validations/create-validators";

interface Props {
  dispatch: AppDispatch;
  disableForm: boolean;
  factories: {
    waardelijstFactory: WaardelijstFactory;
    dropdownInfo: DropdownInfoFactory;
  };
  requiredRefs: HTMLInputElement[];
  mode: "update" | "create";
  data: DossierMetaData;
  actievePublishers: IdentifierWithChildren[];
  dossiertype: IdentifierWithChildren[];
}

export function DossierFormConfig(props: Props): FormConfig {
  const {
    requiredDropdownValidations,
    requiredTextInputValidations,
    textInputValidations,
    textInputValidationsWithMaxLength200,
    textInputValidationsWithMaxLength1000,
  } = createValidators();
  const { data } = props;
  const disableForm = props.disableForm;

  const makeDossierhouderDropdown = (): any => ({
    type: "dropdown",
    id: Teksten.createScreen.dossierhouder.id,
    disabled: disableForm || props.mode === "update",
    tekst: { ...Teksten.createScreen.dossierhouder, tooltip: undefined },
    onUpdated: (identifier: Identifier) => props.dispatch(updatePublisher(identifier)),
    inputRef: (ref: any) => props.requiredRefs.push(ref),
    selected: data.dossierHouder ? [data.dossierHouder] : [],
    categories: props.factories.dropdownInfo.generateCheckboxListSelect(props.actievePublishers, data.dossierHouder),
    validations: requiredDropdownValidations,
    singleSelect: true,
    required: true,
  });

  const makeDossierTypeDropdown = (): any => ({
    type: "dropdown",
    id: Teksten.createScreen.dossiertype.id,
    disabled: disableForm || props.mode === "update",
    tekst: { ...Teksten.createScreen.dossiertype, tooltip: undefined },
    onUpdated: (identifier: Identifier) => props.dispatch(updateDossierType(identifier)),
    inputRef: (ref: any) => props.requiredRefs.push(ref),
    selected: data.type ? [{ id: data.type, label: data.type }] : [],
    categories: props.factories.dropdownInfo.generateCheckboxListSelect(props.dossiertype, {
      id: data.type!,
    }),
    validations: requiredDropdownValidations,
    singleSelect: true,
    required: true,
  });

  const makeTitelsText = (): any => ({
    type: "text",
    label: Teksten.createScreen.titel.label,
    id: Teksten.createScreen.titel.id,
    placeholder: Teksten.createScreen.titel.placeholderDossier,
    value: data.titel,
    disabled: disableForm,
    validations: [...requiredTextInputValidations, ...textInputValidationsWithMaxLength200],
    onChange: (titel: string) => props.dispatch(updateTitel(titel)),
    inputRef: (ref: any) => props.requiredRefs.push(ref),
    required: true,
  });

  const makeIdentifierField = (): any => ({
    type: "text",
    label: Teksten.createScreen.identifier.label,
    id: Teksten.createScreen.identifier.id,
    placeholder: Teksten.createScreen.identifier.placeholder,
    value: data.identificatiekenmerken[0] ?? "",
    disabled: disableForm,
    validations: [...requiredTextInputValidations, ...textInputValidationsWithMaxLength200],
    onChange: (value: string) => props.dispatch(updateIdentifiers([value])),
    inputRef: (ref: any) => props.requiredRefs.push(ref),
    required: true,
  });

  const makeCreatiedatumText = (): any => ({
    type: "text",
    id: Teksten.createScreen.creatiedatum.id,
    disabled: disableForm,
    label: Teksten.createScreen.creatiedatum.label,
    placeholder: Teksten.createScreen.creatiedatum.placeholder,
    value: data.creatieDatum,
    onChange: (val: string) => props.dispatch(updateCreatieDatum(val)),
    validations: [...textInputValidations, ...textInputValidationsWithMaxLength200],
  });

  const makeThemaDropdown = (): any => ({
    type: "dropdown",
    id: Teksten.createScreen.thema.id,
    disabled: disableForm,
    tekst: { ...Teksten.createScreen.thema, tooltip: undefined },
    categories: props.factories.dropdownInfo.generateCategorlyListSelectFromList(
      props.factories.waardelijstFactory.createThemaLijst(),
      data.themas ?? []
    ),
    selected: data.themas ?? [],
    singleSelect: false,
    onUpdated: (id: Identifier) => props.dispatch(updateThemas(id)),
  });

  const makeOmschrijvingTextarea = (): any => ({
    type: "textArea",
    id: Teksten.createScreen.omschrijving.id,
    disabled: disableForm,
    label: Teksten.createScreen.omschrijving.labelDossier,
    placeholder: Teksten.createScreen.omschrijving.placeholder,
    value: data.omschrijving ?? "",
    onChange: (val: string) => props.dispatch(updateOmschrijving(val)),
    validations: [...textInputValidations, ...textInputValidationsWithMaxLength1000],
  });

  const required = [makeDossierhouderDropdown(), makeDossierTypeDropdown(), makeTitelsText(), makeIdentifierField()];
  const optional = [makeCreatiedatumText(), makeThemaDropdown(), makeOmschrijvingTextarea()];

  const formConfig: FormConfig = {
    fields: {
      required,
      optional,
    },
  };

  return formConfig;
}
